import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionGateway } from '../index';
import { formatUtcDate } from '../formatDate';
import { subscriptionStatusBadgeClass, subscriptionStatusLabel } from '../subscriptionStatus';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { EntitlementGate } from '../../../shared/EntitlementGate';
import { FeatureAvailabilityBadge } from '../../../shared/FeatureAvailabilityBadge';
import { isBillingUnavailable } from '../billing/billingErrors';
import { PlanChoiceDialog } from '../billing/PlanChoiceDialog';
import { NextPlanAdds, PlanProcessNote, ScheduledPlanChange } from '../PlanComparisonParts';
import {
  customerFacingFeatures, featureAvailability, groupFeaturesByCategory, inheritanceLabel, planAboveTier, planIncludes, sortPlans,
} from '../planComparison';
import {
  BILLING_OWNERSHIP_OPTIONS, formatCents, planAccessOf,
  type CompanySubscriptionResponse, type FeatureSummary,
  type PlanSummary, type SubscriptionChangeRequestResponse, type SubscriptionEstimateResponse,
} from '../types';
import '../billing.css';

/** Best-effort: is a billing provider configured here? An unknown answer (the lookup failed for another reason, or the
 * caller may not read billing status) is treated as "available" — the server's own answer to the action decides. */
async function providerAvailable(): Promise<boolean> {
  try {
    const status = await subscriptionGateway.getBillingStatus();
    return status.provider.configured;
  } catch (err) {
    return !isBillingUnavailable(err);
  }
}

/** `/settings/subscription` — plan comparison, the plan-by-plan feature matrix, entitlements, and the entry to
 * starting a free trial or subscribing. Billing itself (payment method, invoices, plan changes with a price preview,
 * cancel) lives in the billing center, `/settings/billing`. Every value here is from the real EPN.Subscriptions.Api and
 * the caller's own server-derived company context; nothing is hard-coded or client-only. Prices and the feature matrix
 * are exactly what the API returned. */
export function SubscriptionPage() {
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [features, setFeatures] = useState<FeatureSummary[]>([]);
  const [subscription, setSubscription] = useState<CompanySubscriptionResponse | null>(null);
  const [estimate, setEstimate] = useState<SubscriptionEstimateResponse | null>(null);
  const [history, setHistory] = useState<SubscriptionChangeRequestResponse[]>([]);
  const [providerOk, setProviderOk] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [chosenPlan, setChosenPlan] = useState<PlanSummary | null>(null);
  const closeDialog = useCallback(() => setChosenPlan(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [planList, featureList, current, hist, provider] = await Promise.all([
        subscriptionGateway.listPlans(),
        subscriptionGateway.listFeatures(),
        subscriptionGateway.getCurrentSubscription(),
        subscriptionGateway.listChangeHistory(),
        providerAvailable(),
      ]);
      setPlans(sortPlans(planList));
      setFeatures(featureList);
      setSubscription(current);
      setHistory(hist);
      setProviderOk(provider);

      if (current) {
        setEstimate(await subscriptionGateway.getEstimate());
      }
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load subscription information.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const access = planAccessOf(subscription);

  // The internal demo rows ("Platform Examples") are never part of a customer-facing comparison.
  const customerFeatures = customerFacingFeatures(features);
  const featureGroups = groupFeaturesByCategory(customerFeatures);
  // The existing change-plan flow (with the exact charge shown before confirming) lives on the billing page.
  const changePlanPaused = subscription?.effectiveState === 'PastDueRestricted';
  const nextPlan = subscription ? planAboveTier(plans, subscription.planTier) : null;

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Subscription</h2>
        <Link to="/settings/billing">Billing, invoices &amp; payment method →</Link>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading…</p>}

      {!loading && (
        <>
          <CurrentPlanSummary subscription={subscription} estimate={estimate} />
          {subscription && <ScheduledPlanChange subscription={subscription} plans={plans} />}

          <h3>Plan comparison</h3>
          {(subscription === null || subscription.effectiveState === 'TrialNotStarted') && (
            <p className="muted">
              Start a free 30-day trial with no credit card, or subscribe straight away. Prices are per month, plus taxes shown
              before you pay.
            </p>
          )}
          <PlanProcessNote />
          <div className="plan-cards">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                inherits={inheritanceLabel(plans, plan)}
                isCurrent={subscription?.planCode === plan.code}
                canChoose={!(subscription?.planCode === plan.code && isPaidState(subscription))}
                onChoose={() => setChosenPlan(plan)}
              />
            ))}
          </div>

          {subscription && (
            <NextPlanAdds
              plans={plans}
              features={customerFeatures}
              currentTier={subscription.planTier}
              actionHref={nextPlan && !changePlanPaused ? `/settings/billing?changePlan=${encodeURIComponent(nextPlan.code)}` : null}
            />
          )}

          <h3>What each plan includes</h3>
          <p className="muted">
            "Coming soon" means the capability isn't built yet, on any plan — never an upgrade opportunity.
            "Beta" means it works today and is still being refined. "Pilot only" means it is not open to every company.
            A padlock means it's real and working, just not part of your current plan. HR advisory: included services are defined per plan.
          </p>
          {featureGroups.map(({ category, features: categoryFeatures }) => (
            <div key={category} style={{ marginBottom: 12 }}>
              <strong>{category}</strong>
              <div className="billing-table-wrap">
                <table className="billing-table feature-matrix" aria-label={`${category} features by plan`}>
                  <thead>
                    <tr>
                      <th scope="col">Feature</th>
                      {plans.map((plan) => <th scope="col" key={plan.id}>{plan.name}</th>)}
                      <th scope="col" style={{ textAlign: 'right' }}>Your access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryFeatures.map((f) => (
                      <tr key={f.featureKey}>
                        <td>
                          <span>{f.displayName}</span>
                          {featureAvailability(f) === 'beta' && <> <FeatureAvailabilityBadge readinessStatus={f.readinessStatus} /></>}
                          {f.description && <span className="feature-matrix__detail">{f.description}</span>}
                          {f.implementationNote && <span className="feature-matrix__detail">{f.implementationNote}</span>}
                        </td>
                        {plans.map((plan) => <MatrixCell key={plan.id} feature={f} plan={plan} />)}
                        <td style={{ textAlign: 'right' }}>
                          <EntitlementGate
                            feature={f}
                            access={access}
                            showReadinessBadge={false}
                            onUpgradeClick={(feature) => {
                              const target = plans.find((p) => p.tier === feature.minimumPlanTier);
                              if (target) setChosenPlan(target);
                            }}
                          >
                            <span className="success">Included</span>
                          </EntitlementGate>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <BillingOwnershipSection currentOwnership={subscription?.billingOwnership ?? null} />

          <h3>Change history</h3>
          {history.length === 0 && <p className="muted">No plan changes yet.</p>}
          {history.length > 0 && (
            <table>
              <thead><tr><th>When</th><th>From</th><th>To</th><th>Billing Ownership</th><th>Status</th><th>Requested By</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td>{new Date(h.createdAtUtc).toLocaleString()}</td>
                    <td>{h.fromPlanCode}</td>
                    <td>{h.toPlanCode}</td>
                    <td>{h.requestedBillingOwnership}</td>
                    <td>{h.status}</td>
                    <td className="muted">{h.requestedByEmailSnapshot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3>Invoices and payment method</h3>
          <p className="muted">
            Your invoices, payment method, billing history and billing support are on the{' '}
            <Link to="/settings/billing">Billing page</Link>.
          </p>
        </>
      )}

      {chosenPlan && (
        <PlanChoiceDialog plan={chosenPlan} subscription={subscription} providerAvailable={providerOk} onClose={closeDialog} />
      )}
    </section>
  );
}

function isPaidState(subscription: CompanySubscriptionResponse | null): boolean {
  const state = subscription?.effectiveState;
  return state === 'Active' || state === 'PendingChange' || state === 'PastDueGrace' || state === 'PastDueRestricted';
}

/** One cell of the plan-by-plan matrix. The glyph is decoration; the hidden text states it in words. A feature that is
 * not built yet is "not yet available" on every plan, never a padlock. */
function MatrixCell({ feature, plan }: { feature: FeatureSummary; plan: PlanSummary }) {
  const availability = featureAvailability(feature);
  if (availability === 'coming-soon') {
    return <td><span aria-hidden="true">—</span><span className="sr-only">Not yet available on {plan.name}</span></td>;
  }
  if (availability === 'pilot-only' || availability === 'unavailable') {
    return <td><span aria-hidden="true">—</span><span className="sr-only">Not available on {plan.name} right now</span></td>;
  }
  const inPlan = planIncludes(plan, feature);
  const beta = availability === 'beta' ? ' (Beta)' : '';
  return (
    <td>
      <span aria-hidden="true">{inPlan ? '✓' : '—'}</span>
      <span className="sr-only">{inPlan ? `Available in ${plan.name}${beta}` : `Not available in ${plan.name}`}</span>
    </td>
  );
}

function CurrentPlanSummary({ subscription, estimate }: { subscription: CompanySubscriptionResponse | null; estimate: SubscriptionEstimateResponse | null }) {
  if (!subscription) {
    return (
      <p className="muted">
        This company has no subscription yet. Choose a plan below to start a free trial or subscribe — nothing is enabled by
        default.
      </p>
    );
  }

  return (
    <div>
      <p>
        Current plan: <strong>{subscription.planName}</strong> —{' '}
        <span className={subscriptionStatusBadgeClass(subscription)}>{subscriptionStatusLabel(subscription)}</span>
      </p>
      {subscription.effectiveState === 'TrialActive' && (
        <p className="muted">
          Trial ends {formatUtcDate(subscription.trialEndUtc)}. Live payroll, Direct Deposit, contractor payments,
          and tax payments require completion of the applicable activation, verification, authorization, and
          payroll-readiness requirements. Choose a plan below to subscribe.
        </p>
      )}
      {subscription.effectiveState === 'TrialNotStarted' && (
        <p className="muted">
          Your {subscription.planName} plan is selected. Choose it below to start your {subscription.trialLengthDays}-day free
          trial whenever you are ready — no credit card is needed, and the clock has not begun.
        </p>
      )}
      {subscription.effectiveState === 'TrialExpired' && (
        <p className="muted">
          Your {subscription.trialLengthDays}-day trial has ended. Your setup data remains saved, but plan
          features, live payroll, Direct Deposit, tax filing/payment and report export stay unavailable until you subscribe.
          Choose a plan below to subscribe.
        </p>
      )}
      {(subscription.effectiveState === 'PastDueGrace' || subscription.effectiveState === 'PastDueRestricted') && (
        <p className="muted">
          Your last payment did not go through. <Link to="/settings/billing">Update your payment method</Link> to keep or restore full access.
        </p>
      )}
      {subscription.effectiveState === 'Canceled' && (
        <p className="muted">
          This subscription is canceled. <Link to="/settings/billing">Reactivate it on the billing page</Link>, or choose a plan below to subscribe again.
        </p>
      )}
      <div className="stat-grid">
        <div className="stat-card"><span className="stat-value">{formatCents(subscription.baseMonthlyFeeCents)}</span><span className="muted">Base monthly fee</span></div>
        <div className="stat-card"><span className="stat-value">{formatCents(subscription.perEmployeeMonthlyFeeCents)}</span><span className="muted">Per billable employee</span></div>
        <div className="stat-card"><span className="stat-value">{estimate?.billableHeadcount ?? '—'}</span><span className="muted">Billable headcount</span></div>
        <div className="stat-card"><span className="stat-value">{estimate ? formatCents(estimate.estimatedMonthlyTotalCents) : '—'}</span><span className="muted">Estimated monthly total</span></div>
      </div>
    </div>
  );
}

function PlanCard({ plan, inherits, isCurrent, canChoose, onChoose }: { plan: PlanSummary; inherits: string | null; isCurrent: boolean; canChoose: boolean; onChoose: () => void }) {
  return (
    <div className={isCurrent ? 'plan-card plan-card--current' : 'plan-card'}>
      <strong>{plan.name}</strong>
      <p className="muted">{plan.description}</p>
      {inherits && <p className="plan-card-inherits">{inherits}</p>}
      <div className="plan-card-price">{formatCents(plan.baseMonthlyFeeCents)}<span className="muted"> /mo base</span></div>
      <p className="muted">{formatCents(plan.perEmployeeMonthlyFeeCents)} per billable employee/mo</p>
      {isCurrent && <span className="badge badge--ok" style={{ alignSelf: 'flex-start' }}>Current plan</span>}
      {canChoose && <button type="button" onClick={onChoose} aria-label={`Choose ${plan.name} plan`}>Choose plan</button>}
      {isCurrent && !canChoose && (
        <Link to="/settings/billing">Manage on the billing page</Link>
      )}
    </div>
  );
}

function BillingOwnershipSection({ currentOwnership }: { currentOwnership: string | null }) {
  const [selected, setSelected] = useState<string | null>(currentOwnership);

  const selectedOption = BILLING_OWNERSHIP_OPTIONS.find((o) => o.code === selected) ?? null;

  return (
    <div>
      <h3>Who manages this subscription?</h3>
      <div className="billing-ownership-cards">
        {BILLING_OWNERSHIP_OPTIONS.map((option) => (
          <button
            key={option.code}
            type="button"
            className={selected === option.code ? 'billing-ownership-card billing-ownership-card--selected' : 'billing-ownership-card'}
            aria-pressed={selected === option.code}
            onClick={() => setSelected(option.code)}
          >
            <strong>{option.title}</strong>
            <span className="billing-ownership-card-bestfor">Best for: {option.bestFor}</span>
            <span className="muted">{option.description}</span>
          </button>
        ))}
      </div>

      {selectedOption && (
        <div className="billing-ownership-summary">
          <p>
            <strong>Billing owner:</strong> {selectedOption.billingOwner} · <strong>Invoice recipient:</strong>{' '}
            {selectedOption.invoiceRecipient} · <strong>Plan management:</strong> {selectedOption.planManagement}
          </p>
          {selectedOption.code === 'PartnerRewards' && (
            <p className="muted">
              Partner reward percentage: not yet configured — subject to approved Escochex partner-program
              terms. See <code>docs/subscriptions-staging-limitations.md</code>.
            </p>
          )}
        </div>
      )}

      {/* EPN-80: billing contact management lives on the dedicated billing screen, so there is exactly one place to edit it. */}
      <p className="muted">
        <Link to="/settings/billing">Manage billing contact &amp; invoices →</Link>
      </p>
    </div>
  );
}
